import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DrawerDetails, StationBoardJourneysType } from '@/types';

import DrawerContainer from '@/components/drawers/item-drawer/drawer-container';
import DrawerContent from '@/components/drawers/item-drawer/drawer-content';
import DrawerHandle from '@/components/drawers/item-drawer/drawer-handle';
import InfoLoader from '@/components/shared/loader/info-loader';
import { useContentHeight } from '@/hooks/use-content-height';
import { useDrawerSearchParams } from '@/hooks/use-drawer-search-params';
import { useGetIncidentsByStop } from '@/hooks/use-get-incidents-by-stop';
import useSocketSubscription from '@/hooks/use-socket-subscription';
import useStationDrawerData from '@/hooks/use-station-drawer-data';
import useGetTrainDetails from '@/hooks/use-train-drawer-data';
import { TODAY } from '@/utils/constants';
import { getJourneyDisplayName } from '@/utils/helpers/get-journey-display-name';
import getStationBoardItem from '@/utils/helpers/get-station-board-items';
import { getStationBoardListItems } from '@/utils/helpers/get-station-board-list-items';
import isIncidentActiveOnDate from '@/utils/helpers/is-incident-active-on-date';

interface DrawerContent {
  details: DrawerDetails[];
}

interface ItemDrawerProps {
  isToastVisible?: boolean;
  onHeightChange?: (height: number) => void;
}

const ItemDrawer: FC<ItemDrawerProps> = ({ isToastVisible, onHeightChange }) => {
  const { t } = useTranslation();

  const contentRef = useContentHeight({ onHeightChange, offset: 30 });

  const { pinnedItemId, pinnedItemType, isStation, handleRemoveDrawer, handleToggleLockPin } =
    useDrawerSearchParams();

  const stationId = isStation ? pinnedItemId : null;
  const { stationData, isStationDataLoading } = useStationDrawerData(stationId);
  const { data: trainData, isTrainInfoLoading } = useGetTrainDetails(pinnedItemId, {
    enabled: !isStation,
  });

  const { data: incidents = [] } = useGetIncidentsByStop(stationId);

  const incidentCount = useMemo(() => {
    return incidents.filter((incident) =>
      incident.activePeriod?.some(({ start, end }) => isIncidentActiveOnDate(start, end, TODAY))
    ).length;
  }, [incidents]);

  const socketParams = { stop_id: pinnedItemId };

  const { data: nextDepartures, isLoading: isStationBoardLoading } =
    useSocketSubscription<StationBoardJourneysType>('station_board', {
      params: socketParams,
      enabled: isStation && !!pinnedItemId,
      filter: (incoming) => incoming.stopId === pinnedItemId,
    });

  const isLoading =
    isStationDataLoading || isTrainInfoLoading || (isStation && isStationBoardLoading);

  const { arrivals, departures } = useMemo(
    () => getStationBoardListItems(nextDepartures),
    [nextDepartures]
  );

  const { nextItems: rawNextArrivals } = getStationBoardItem(arrivals, true);
  const { nextItems: rawNextADepartures } = getStationBoardItem(departures, false);

  const firstArrival = rawNextArrivals[0]?.journey;
  const firstDeparture = rawNextADepartures[0]?.journey;

  const { journey: nextArrivalJourney } = firstArrival ?? {};
  const { journey: nextDepartureJourney } = firstDeparture ?? {};

  const drawerContent = useMemo((): DrawerContent | null => {
    const incidentDetail: DrawerDetails[] =
      incidentCount > 0
        ? [
            {
              type: 'incident',
              content: t('drawer.incidents', { count: incidentCount }),
            },
          ]
        : [];

    if (isStation && stationData) {
      const nextArrivalText = nextArrivalJourney
        ? `${nextArrivalJourney.lineNumber} ${nextArrivalJourney.journeyNumber}`
        : 'Unavailable';

      const nextDepartureText = nextDepartureJourney
        ? `${nextDepartureJourney.lineNumber} ${nextDepartureJourney.journeyNumber}`
        : 'Unavailable';

      return {
        details: [
          {
            type: 'station',
            content: <span className="font-semibold">{stationData.designationOfficial}</span>,
          },
          {
            type: 'route',
            content: (
              <>
                <span className="pr-1">{t('drawer.next_arrival')}</span>
                <span className="font-semibold">{nextArrivalText}</span>
              </>
            ),
          },
          {
            type: 'route',
            content: (
              <>
                <span className="pr-1">{t('drawer.next_departure')}</span>
                <span className="font-semibold">{nextDepartureText}</span>
              </>
            ),
          },
          ...incidentDetail,
        ],
      };
    }

    if (!isStation && trainData) {
      const journeyDisplayName = getJourneyDisplayName(trainData);

      return {
        details: [
          {
            type: 'route',
            content: (
              <span className="font-semibold">
                {`${journeyDisplayName} ${trainData.journeyNumber}`}
              </span>
            ),
            deviation: trainData.deviation,
          },
          {
            type: 'current-station',
            content: trainData.currentStation,
          },
          {
            type: 'next-station',
            content: (
              <>
                <span className="pr-1">{t('drawer.next_station')}</span>
                <span className="font-semibold">{trainData.nextStation}</span>
              </>
            ),
          },
        ],
      };
    }

    return null;
  }, [isStation, stationData, trainData, firstArrival, firstDeparture, incidentCount]);

  if (!pinnedItemId || !pinnedItemType) {
    return null;
  }

  return (
    <DrawerContainer isToastVisible={isToastVisible}>
      <DrawerHandle onClick={handleRemoveDrawer} />
      <div ref={contentRef}>
        {isLoading || !drawerContent ? (
          <div className="px-4 pb-4">
            <InfoLoader />
          </div>
        ) : (
          <DrawerContent
            details={drawerContent.details}
            isLocked={!!pinnedItemId}
            onToggleLock={handleToggleLockPin}
          />
        )}
      </div>
    </DrawerContainer>
  );
};

export default ItemDrawer;
